import DataTable from 'react-data-table-component';
import { useState, useEffect } from "react";
import { LOG_RECORDS_MANAGER } from '../components/log-records-manager';
import { TOAST_FAIL } from '../components/toasters';
import ExcelJS from "exceljs";

function ArchiveLogList({ onRefresh }) {
    const [archiveLogs, setArchiveLogs] = useState([]);
    const [filterText, setFilterText] = useState("");  // State for the filter text
    const [searchText, setSearchText] = useState("");
    const [loader, setLoader] = useState("Restore");
    const [loader2, setLoader2] = useState("Export");
    const { ARCHIVED_LOGS_FETCH, RESTORE_VEHICLE_LOGS, DELETE_ARCHIVE_LOGS } = LOG_RECORDS_MANAGER();

    const columns = [
        {
            name: 'Plate Number',
            selector: row => {
                const plate = String(row.plate_number);
                const middle = plate.slice(1, -1).replace(/./g, '*');
                return `${plate[0]}${middle}${plate.slice(-1)}`;  // Show first and last characters
            },
            sortable: true,
        },
        {
            name: 'Owner Name',
            selector: row => row.username,
            sortable: false,
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: false,
        },
        {
            name: 'Identity',
            selector: row => row.identity,
            sortable: true,
        },
        {
            name: "Time In",
            selector: row => formatDateTime(row.time_in),
            sortable: true,
        },
        {
            name: "Time Out",
            selector: row => formatDateTime(row.time_out),
            sortable: true,
        }
    ];

    const customStyles = {
        table : {
            style : {
                height: '85vh',
            }
        },
        headCells: {
            style: {
                fontWeight: 'bold',  // Make headers bold
                backgroundColor: '#191D80',  // Background color for the headers
                color: '#fff',  // Font color for the headers
                paddingLeft: '2.5%',
                justifyContent: 'center',
            },
        },
        cells: {
            style: {
                justifyContent: 'center',
            }
        }
    };

    // Helper function to format date
    const formatDateTime = (dateString) => {
        if(!dateString) {
            return "N/A";
        }

        const options = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric', 
            hour: 'numeric', 
            minute: 'numeric', 
            hour12: true 
        };
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', options).format(date);
    };

    // Fetch all vehicle logs data
    const fetchData = async () => {
        const archiveLogList = await ARCHIVED_LOGS_FETCH();
        setArchiveLogs(archiveLogList);
    };

    useEffect(() => {
        fetchData();
    }, [onRefresh]);

    // Filtered data based on input
    const filteredLogs = archiveLogs.filter(log => {
        let matchesDate = true;
        // **Date Filtering**
        if (filterText) {
            const [month, year] = filterText.split('-');
            const formattedFilterMonthYear = new Date(`${month}-${year}-01`).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long'
            });
    
            const formatDateToMonthYear = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long'
                });
            };
    
            const timeInMonthYear = formatDateToMonthYear(log.time_in);
            const timeOutMonthYear = formatDateToMonthYear(log.time_out);
    
            matchesDate = timeInMonthYear === formattedFilterMonthYear || timeOutMonthYear === formattedFilterMonthYear;
        }

        // **Search Filtering**
        const matchesSearch = searchText === "" ||
            log.plate_number.toLowerCase().includes(searchText.toLowerCase()) ||
            log.username?.toLowerCase().includes(searchText.toLowerCase()) ||
            log.identity.toLowerCase().includes(searchText.toLowerCase());

        return (filterText ? matchesDate : true) && matchesSearch;
    });

    const restore = async(event) => {
        event.preventDefault();
        setLoader("Restoring...");

        if(filterText === "") {
            TOAST_FAIL("Please filter first the log record you want to archive");
        } else {
            await RESTORE_VEHICLE_LOGS(filterText);
        }
        setLoader("Restore");
        fetchData();
    }

    const export_file = async (event) => {
        event.preventDefault();
        setLoader2("Exporting...");
    
        if (filterText === "") {
            TOAST_FAIL("Please filter the log records before exporting.");
            setLoader2("Export");
            return;
        }
    
        // Prepare data for export
        const dataToExport = filteredLogs.map((log) => ({
            "Owner Name": log.username,
            "Plate Number": log.plate_number,
            "Email": log.email,
            "Identity": log.identity,
            "Time In": formatDateTime(log.time_in),
            "Time Out": formatDateTime(log.time_out),
        }));
    
        if (dataToExport.length === 0) {
            TOAST_FAIL("No records found for the selected date.");
            setLoader2("Export");
            return;
        }
    
        // Create a new workbook and worksheet
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Vehicle Logs");
    
        // Add a title row
        const [year, month] = filterText.split('-');
        const monthYear = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(year, month - 1));
        const titleRow = worksheet.addRow([`Log Records of ${monthYear}`]);
        titleRow.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
        titleRow.alignment = { horizontal: "center" };
        titleRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "#6868AA" } };
    
        // Merge cells for the title
        worksheet.mergeCells(1, 1, 1, Object.keys(dataToExport[0]).length);
    
        // Add column headers
        const headers = Object.keys(dataToExport[0]);
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
        headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF191D80" } };
        headerRow.alignment = { horizontal: "center" };
    
        // Add data rows
        dataToExport.forEach((rowData) => {
            worksheet.addRow(Object.values(rowData));
        });
    
        // Auto-fit column widths
        worksheet.columns.forEach((column, index) => {
            const header = headers[index];
            const maxLength = Math.max(
                header.length,
                ...dataToExport.map((row) => String(row[header] || "").length)
            );
            column.width = maxLength + 2; // Add padding
        });
    
        // Export the workbook
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Archived_Logs_${filterText.replace("-", "_")}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    
        setLoader2("Export");
    };     

    return (
        <div className="logs-table">
            <DataTable
                columns={columns}
                data={filteredLogs}  // Use the filtered data
                customStyles={customStyles}
                fixedHeader
                pagination
                responsive
                subHeader
                subHeaderComponent={(
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <h5 className="fw-bold">Archived Logs</h5>

                        <div>
                            <input className="archive-search-field ps-2" 
                                   placeholder="Search"
                                   value={searchText} 
                                   onChange={e => setSearchText(e.target.value)} />
                        </div>

                        <div className="d-flex align-items-center">
                            <button
                                className="btn btn-info me-2"
                                onClick={ export_file }
                                disabled={loader2 === "Exporting..."}>{ loader2 }</button>
                            <button
                                className="btn btn-success me-2"
                                onClick={ restore }
                                disabled={loader === "Restoring..."}>{ loader }</button>
                            <input
                                type="month"
                                className="border-2 p-1 rounded-3 bg-transparent"
                                placeholder="Filter logs"
                                value={filterText}
                                onChange={e => setFilterText(e.target.value)}  // Automatically filter data
                            />
                        </div>

                    </div>
                )}
            />
        </div>
    );

}

export default ArchiveLogList;